// src/pages/ImpressaoCracha.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Tooltip,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  Slider
} from '@mui/material';
import {
  Print as PrintIcon,
  Visibility as PreviewIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  PhotoCamera as PhotoIcon,
  QrCode as QrCodeIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';

const ImpressaoCracha = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionariosSelecionados, setFuncionariosSelecionados] = useState([]);
  const [filtros, setFiltros] = useState({
    busca: '',
    setor: ''
  });
  const [dialogPreview, setDialogPreview] = useState(false);
  const [funcionarioPreview, setFuncionarioPreview] = useState(null);
  const [configuracoes, setConfiguracoes] = useState({
    incluirFoto: true,
    incluirQRCode: true,
    incluirSetor: true,
    incluirDataValidade: true,
    tamanhoCracha: 'medio', // pequeno, medio, grande
    corTema: '#9C27B0'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const crachaRef = useRef(null);

  const { api } = useAuth();

  const tamanhosDisponiveis = [
    { value: 'pequeno', label: 'Pequeno (85x54mm)', width: 320, height: 200 },
    { value: 'medio', label: 'Médio (100x65mm)', width: 380, height: 245 },
    { value: 'grande', label: 'Grande (120x80mm)', width: 450, height: 300 }
  ];

  const coresDisponiveis = [
    { value: '#9C27B0', label: 'Roxo', color: '#9C27B0' },
    { value: '#2196F3', label: 'Azul', color: '#2196F3' },
    { value: '#4CAF50', label: 'Verde', color: '#4CAF50' },
    { value: '#FF9800', label: 'Laranja', color: '#FF9800' },
    { value: '#F44336', label: 'Vermelho', color: '#F44336' },
    { value: '#607D8B', label: 'Cinza', color: '#607D8B' }
  ];

  const carregarFuncionarios = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get('/funcionarios');
      setFuncionarios(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar funcionários:', err);
      setError('Erro ao carregar lista de funcionários');
      
      // Fallback com dados mockados
      const mockFuncionarios = [
        {
          id: 1,
          nome: 'João Silva Santos',
          cpf: '123.456.789-01',
          codigo: '1234',
          setor: 'Dermato'
        },
        {
          id: 2,
          nome: 'Maria Santos Oliveira',
          cpf: '987.654.321-09',
          codigo: '9876',
          setor: 'Farmacêutica'
        },
        {
          id: 3,
          nome: 'Pedro Costa Lima',
          cpf: '111.222.333-44',
          codigo: '1112',
          setor: 'Xarope'
        }
      ];
      
      setFuncionarios(mockFuncionarios);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarFuncionarios();
  }, []);

  // Filtrar funcionários
  const funcionariosFiltrados = funcionarios.filter(funcionario => {
    const matchBusca = !filtros.busca || 
      funcionario.nome.toLowerCase().includes(filtros.busca.toLowerCase()) ||
      funcionario.codigo.includes(filtros.busca);
    
    const matchSetor = !filtros.setor || funcionario.setor === filtros.setor;
    
    return matchBusca && matchSetor;
  });

  // Obter setores únicos
  const setoresUnicos = [...new Set(funcionarios.map(f => f.setor))];

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPage(0);
  };

  const handleSelecionarFuncionario = (funcionarioId, selecionado) => {
    if (selecionado) {
      setFuncionariosSelecionados(prev => [...prev, funcionarioId]);
    } else {
      setFuncionariosSelecionados(prev => prev.filter(id => id !== funcionarioId));
    }
  };

  const handleSelecionarTodos = (selecionado) => {
    if (selecionado) {
      setFuncionariosSelecionados(funcionariosFiltrados.map(f => f.id));
    } else {
      setFuncionariosSelecionados([]);
    }
  };

  const gerarDataValidade = () => {
    const data = new Date();
    data.setFullYear(data.getFullYear() + 1); // Válido por 1 ano
    return data.toLocaleDateString('pt-BR');
  };

  const ComponenteCracha = ({ funcionario, preview = false }) => {
    const tamanho = tamanhosDisponiveis.find(t => t.value === configuracoes.tamanhoCracha);
    
    return (
      <div
        ref={preview ? crachaRef : null}
        style={{
          width: tamanho.width,
          height: tamanho.height,
          background: `linear-gradient(135deg, ${configuracoes.corTema} 0%, ${configuracoes.corTema}dd 100%)`,
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          color: 'white',
          padding: '20px',
          margin: preview ? '0' : '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Header da empresa */}
        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            marginBottom: '5px',
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
          }}>
            SISTEMA CONTROLE
          </div>
          <div style={{ 
            fontSize: '10px', 
            opacity: 0.9,
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Controle de Requisições
          </div>
        </div>

        {/* Seção principal com foto e dados */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px',
          flex: 1
        }}>
          {/* Foto do funcionário */}
          {configuracoes.incluirFoto && (
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              fontWeight: 'bold',
              color: 'white',
              border: '3px solid rgba(255,255,255,0.3)'
            }}>
              {funcionario.nome.charAt(0).toUpperCase()}
            </div>
          )}

          {/* Dados do funcionário */}
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: 'bold', 
              marginBottom: '8px',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              {funcionario.nome}
            </div>
            
            <div style={{ 
              fontSize: '14px', 
              marginBottom: '5px',
              opacity: 0.9
            }}>
              <strong>Código:</strong> {funcionario.codigo}
            </div>
            
            {configuracoes.incluirSetor && (
              <div style={{ 
                fontSize: '14px', 
                marginBottom: '5px',
                opacity: 0.9
              }}>
                <strong>Setor:</strong> {funcionario.setor}
              </div>
            )}
          </div>
        </div>

        {/* Footer com data de validade e QR Code */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginTop: '15px',
          paddingTop: '10px',
          borderTop: '1px solid rgba(255,255,255,0.3)'
        }}>
          {configuracoes.incluirDataValidade && (
            <div style={{ fontSize: '10px', opacity: 0.8 }}>
              Válido até: {gerarDataValidade()}
            </div>
          )}
          
          {configuracoes.incluirQRCode && (
            <div style={{
              width: '40px',
              height: '40px',
              background: 'white',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: configuracoes.corTema,
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              QR
            </div>
          )}
        </div>

        {/* Decoração de fundo */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          zIndex: 0
        }} />
        
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          zIndex: 0
        }} />
      </div>
    );
  };

  const handlePreviewCracha = (funcionario) => {
    setFuncionarioPreview(funcionario);
    setDialogPreview(true);
  };

  const handleImprimirSelecionados = () => {
    if (funcionariosSelecionados.length === 0) {
      setError('Selecione pelo menos um funcionário para imprimir');
      return;
    }

    const funcionariosParaImprimir = funcionarios.filter(f => 
      funcionariosSelecionados.includes(f.id)
    );

    gerarPDFCrachas(funcionariosParaImprimir);
  };

  const gerarPDFCrachas = (funcionariosParaImprimir) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const tamanho = tamanhosDisponiveis.find(t => t.value === configuracoes.tamanhoCracha);
    
    // Converter pixels para mm (aproximadamente)
    const larguraMM = tamanho.width * 0.26458; // 1px ≈ 0.26458mm
    const alturaMM = tamanho.height * 0.26458;
    
    const margemX = 10;
    const margemY = 10;
    const espacamentoX = 5;
    const espacamentoY = 5;
    
    // Calcular quantos crachás cabem por página
    const crachasPorLinha = Math.floor((210 - 2 * margemX) / (larguraMM + espacamentoX));
    const linhasPorPagina = Math.floor((297 - 2 * margemY) / (alturaMM + espacamentoY));
    const crachasPorPagina = crachasPorLinha * linhasPorPagina;

    funcionariosParaImprimir.forEach((funcionario, index) => {
      if (index > 0 && index % crachasPorPagina === 0) {
        pdf.addPage();
      }

      const indicePagina = index % crachasPorPagina;
      const linha = Math.floor(indicePagina / crachasPorLinha);
      const coluna = indicePagina % crachasPorLinha;
      
      const x = margemX + coluna * (larguraMM + espacamentoX);
      const y = margemY + linha * (alturaMM + espacamentoY);

      // Desenhar retângulo do crachá
      pdf.setFillColor(configuracoes.corTema);
      pdf.roundedRect(x, y, larguraMM, alturaMM, 3, 3, 'F');
      
      // Título da empresa
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'bold');
      pdf.text('SISTEMA CONTROLE', x + larguraMM/2, y + 8, { align: 'center' });
      
      // Nome do funcionário
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      pdf.text(funcionario.nome, x + larguraMM/2, y + 20, { align: 'center' });
      
      // Código
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Código: ${funcionario.codigo}`, x + 5, y + 28);
      
      // Setor
      if (configuracoes.incluirSetor) {
        pdf.text(`Setor: ${funcionario.setor}`, x + 5, y + 35);
      }
      
      // Data de validade
      if (configuracoes.incluirDataValidade) {
        pdf.setFontSize(6);
        pdf.text(`Válido até: ${gerarDataValidade()}`, x + 5, y + alturaMM - 5);
      }
    });

    pdf.save(`crachas_${new Date().toISOString().split('T')[0]}.pdf`);
    setSuccess(`${funcionariosParaImprimir.length} crachá(s) gerado(s) com sucesso!`);
  };

  // Dados paginados
  const funcionariosPaginados = funcionariosFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(funcionariosFiltrados.length / rowsPerPage);

  return (
    <Box>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #795548 0%, #5D4037 100%)', color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🎫 Impressão de Crachás
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Gere e imprima crachás personalizados para os funcionários
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Atualizar lista">
              <IconButton onClick={carregarFuncionarios} sx={{ color: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleImprimirSelecionados}
              startIcon={<PrintIcon />}
              disabled={funcionariosSelecionados.length === 0}
              sx={{ 
                bgcolor: '#4CAF50', 
                '&:hover': { bgcolor: '#45a049' }
              }}
            >
              Imprimir ({funcionariosSelecionados.length})
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Painel de Configurações */}
        <Grid item xs={12} md={4}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                ⚙️ Configurações do Crachá
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {/* Tamanho do crachá */}
              <TextField
                fullWidth
                select
                label="Tamanho do Crachá"
                value={configuracoes.tamanhoCracha}
                onChange={(e) => setConfiguracoes(prev => ({ ...prev, tamanhoCracha: e.target.value }))}
                sx={{ mb: 2 }}
              >
                {tamanhosDisponiveis.map((tamanho) => (
                  <MenuItem key={tamanho.value} value={tamanho.value}>
                    {tamanho.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Cor do tema */}
              <TextField
                fullWidth
                select
                label="Cor do Tema"
                value={configuracoes.corTema}
                onChange={(e) => setConfiguracoes(prev => ({ ...prev, corTema: e.target.value }))}
                sx={{ mb: 2 }}
              >
                {coresDisponiveis.map((cor) => (
                  <MenuItem key={cor.value} value={cor.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: cor.color
                        }}
                      />
                      {cor.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* Opções de conteúdo */}
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Conteúdo do Crachá:
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracoes.incluirFoto}
                    onChange={(e) => setConfiguracoes(prev => ({ ...prev, incluirFoto: e.target.checked }))}
                  />
                }
                label="Incluir foto (placeholder)"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracoes.incluirSetor}
                    onChange={(e) => setConfiguracoes(prev => ({ ...prev, incluirSetor: e.target.checked }))}
                  />
                }
                label="Incluir setor"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracoes.incluirQRCode}
                    onChange={(e) => setConfiguracoes(prev => ({ ...prev, incluirQRCode: e.target.checked }))}
                  />
                }
                label="Incluir QR Code"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracoes.incluirDataValidade}
                    onChange={(e) => setConfiguracoes(prev => ({ ...prev, incluirDataValidade: e.target.checked }))}
                  />
                }
                label="Incluir data de validade"
              />

              {/* Preview do crachá */}
              {funcionarios.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Preview:
                  </Typography>
                  <Box sx={{ 
                    transform: 'scale(0.6)', 
                    transformOrigin: 'top left',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    width: 'fit-content'
                  }}>
                    <ComponenteCracha funcionario={funcionarios[0]} />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Lista de Funcionários */}
        <Grid item xs={12} md={8}>
          {/* Filtros */}
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                🔍 Filtros e Seleção
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Buscar funcionário"
                    placeholder="Nome ou código..."
                    value={filtros.busca}
                    onChange={(e) => handleFiltroChange('busca', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Setor"
                    value={filtros.setor}
                    onChange={(e) => handleFiltroChange('setor', e.target.value)}
                  >
                    <MenuItem value="">Todos os setores</MenuItem>
                    {setoresUnicos.map((setor) => (
                      <MenuItem key={setor} value={setor}>
                        {setor}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={funcionariosSelecionados.length === funcionariosFiltrados.length && funcionariosFiltrados.length > 0}
                        indeterminate={funcionariosSelecionados.length > 0 && funcionariosSelecionados.length < funcionariosFiltrados.length}
                        onChange={(e) => handleSelecionarTodos(e.target.checked)}
                      />
                    }
                    label="Selecionar todos"
                  />
                </Grid>
              </Grid>
              
              {funcionariosSelecionados.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {funcionariosSelecionados.length} funcionário(s) selecionado(s) para impressão
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Tabela de funcionários */}
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                👥 Lista de Funcionários
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : funcionariosFiltrados.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Nenhum funcionário encontrado
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>
                            Selecionar
                          </th>
                          <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>
                            Funcionário
                          </th>
                          <th style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>
                            Código
                          </th>
                          <th style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>
                            Setor
                          </th>
                          <th style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {funcionariosPaginados.map((funcionario, index) => (
                          <tr 
                            key={funcionario.id}
                            style={{ 
                              backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9'
                            }}
                          >
                            <td style={{ padding: '15px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                              <Checkbox
                                checked={funcionariosSelecionados.includes(funcionario.id)}
                                onChange={(e) => handleSelecionarFuncionario(funcionario.id, e.target.checked)}
                              />
                            </td>
                            <td style={{ padding: '15px', borderBottom: '1px solid #eee' }}>
                              <Box display="flex" alignItems="center" gap={2}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: configuracoes.corTema,
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {funcionario.nome.charAt(0).toUpperCase()}
                                </Box>
                                <Typography fontWeight="bold">
                                  {funcionario.nome}
                                </Typography>
                              </Box>
                            </td>
                            <td style={{ padding: '15px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                              <Chip
                                label={funcionario.codigo}
                                color="primary"
                                size="small"
                                sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                              />
                            </td>
                            <td style={{ padding: '15px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                              <Chip
                                label={funcionario.setor}
                                color="secondary"
                                size="small"
                              />
                            </td>
                            <td style={{ padding: '15px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                              <Tooltip title="Visualizar crachá">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handlePreviewCracha(funcionario)}
                                  color="primary"
                                >
                                  <PreviewIcon />
                                </IconButton>
                              </Tooltip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                  
                  {/* Paginação */}
                  <Box sx={{
                    p: 2,
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <Typography color="text.secondary">
                      Mostrando {page * rowsPerPage + 1} a {Math.min((page + 1) * rowsPerPage, funcionariosFiltrados.length)} de {funcionariosFiltrados.length} funcionários
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={2}>
                      <TextField
                        select
                        size="small"
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value));
                          setPage(0);
                        }}
                        SelectProps={{ native: true }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </TextField>
                      
                      <Button
                        onClick={() => setPage(Math.max(0, page - 1))}
                        disabled={page === 0}
                        size="small"
                      >
                        Anterior
                      </Button>
                      
                      <Typography>
                        {page + 1} de {totalPages}
                      </Typography>
                      
                      <Button
                        onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                        disabled={page >= totalPages - 1}
                        size="small"
                      >
                        Próxima
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog de Preview do Crachá */}
      <Dialog 
        open={dialogPreview} 
        onClose={() => setDialogPreview(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <BadgeIcon color="primary" />
              Preview do Crachá - {funcionarioPreview?.nome}
            </Box>
            <IconButton onClick={() => setDialogPreview(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            p: 2
          }}>
            {funcionarioPreview && (
              <ComponenteCracha funcionario={funcionarioPreview} preview={true} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (funcionarioPreview) {
                gerarPDFCrachas([funcionarioPreview]);
                setDialogPreview(false);
              }
            }}
            variant="contained"
            startIcon={<PrintIcon />}
          >
            Imprimir Este Crachá
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImpressaoCracha;